import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
} from '@nextui-org/react';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { supabase } from 'common/src/supabase';
import { getSettlements } from 'common/src/api/settlements';
import { getAllExporters } from 'common/src/api/exporters';

interface RecalculateMarketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (settlements: any) => void;
}

export const RecalculateMarketModal = ({
  isOpen,
  onClose,
  onSuccess,
}: RecalculateMarketModalProps) => {
  const [selectedWeeks, setSelectedWeeks] = useState<number>(1);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [exporters, setExporters] = useState<any[]>([]);
  const [selectedExporter, setSelectedExporter] = useState<string>('all');

  useEffect(() => {
    const fetchExporters = async () => {
      const exportersData = await getAllExporters();
      if (exportersData) {
        setExporters(exportersData);
      }
    };
    fetchExporters();
  }, []);

  const handleRecalculateSettlements = async () => {
    setIsRecalculating(true);
    let totalProcessed = 0;
    let totalDetails = 0;
    let totalWithMarketData = 0;

    try {
      const hours = selectedWeeks * 168;
      const specificExporterId =
        selectedExporter === 'all' ? null : +selectedExporter;
      const batchSize = 30; // Procesar 30 liquidaciones por vez

      let hasMore = true;
      let currentOffset = 0;

      while (hasMore) {
        const { data, error } = await supabase.rpc(
          'update_settlements_market_data',
          {
            hours_ago: hours,
            specific_exporter_id: specificExporterId,
            batch_limit: batchSize,
            batch_offset: currentOffset,
          }
        );

        if (error) throw error;

        if (data.success) {
          totalProcessed += data.settlements_processed;
          totalDetails += data.details_processed;
          totalWithMarketData += data.details_with_market_data || 0;
          hasMore = data.has_more;
          currentOffset = data.next_offset;

          // Mostrar progreso intermedio
          if (hasMore) {
            toast.info(
              `Procesando: ${totalProcessed} liquidaciones, ${totalDetails} detalles...`
            );
          }
        } else {
          console.log('data', data);
          throw new Error(data.error);
        }
      }

      toast.success(
        `Recálculo completado: ${totalProcessed} liquidaciones y ${totalDetails} detalles procesados (${totalWithMarketData} con datos de mercado)`
      );

      const updatedSettlements = await getSettlements();
      if (updatedSettlements) {
        onSuccess(updatedSettlements);
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al recalcular el mercado');
    } finally {
      setIsRecalculating(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>Recalcular Mercado</ModalHeader>
        <ModalBody>
          <div className='space-y-4'>
            <Autocomplete
              label='Exportadora'
              value={selectedExporter}
              onSelectionChange={(e) => {
                console.log(e);
                setSelectedExporter(e);
              }}
              className='w-full'
            >
              <AutocompleteItem key='all' value='all'>
                Todas las exportadoras
              </AutocompleteItem>
              {exporters.map((exporter) => (
                <AutocompleteItem
                  key={exporter.id}
                  value={exporter.id.toString()}
                >
                  {exporter.name}
                </AutocompleteItem>
              ))}
            </Autocomplete>

            <Select
              label='Período a recalcular'
              value={selectedWeeks.toString()}
              onChange={(e) => setSelectedWeeks(Number(e.target.value))}
              defaultSelectedKeys={['1']}
            >
              <SelectItem key='1' value='1'>
                1 semana
              </SelectItem>
              <SelectItem key='2' value='2'>
                2 semanas
              </SelectItem>
              <SelectItem key='4' value='4'>
                1 mes
              </SelectItem>
              <SelectItem key='12' value='12'>
                3 meses
              </SelectItem>
              <SelectItem key='24' value='24'>
                6 meses
              </SelectItem>
              <SelectItem key='52' value='52'>
                1 año
              </SelectItem>
            </Select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={onClose}>
            Cancelar
          </Button>
          <Button
            color='primary'
            onPress={handleRecalculateSettlements}
            isLoading={isRecalculating}
          >
            Recalcular
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RecalculateMarketModal;
